
import { FormFieldWrapper } from '@/components/ui/form-field-wrapper';
import { Input } from '@/components/ui/input';
import { ColorPicker } from '@/components/ui/color-picker';
import { MoneyInput } from '@/components/ui/money-input';
import { CardAccountField } from './CardAccountField';
import { useTranslation } from '@/hooks/useTranslation';
import { formPlaceholders } from '@/data/formPlaceholders';

interface CardFormData {
  name: string;
  bank: string;
  limit: string;
  closingDate: string;
  dueDate: string;
  color: string;
  accountId: string;
}

interface CardFormFieldsProps {
  formData: CardFormData;
  errors: { [key: string]: string };
  nameError: string;
  onNameChange: (value: string) => void;
  onFieldChange: (field: keyof CardFormData, value: string) => void;
  onFieldBlur?: (field: keyof CardFormData) => void;
  getNameErrorType: (hasInteracted?: boolean) => 'default' | 'warning' | 'popup';
  getNameErrorMessage: (hasInteracted?: boolean) => string;
  touchedFields?: Record<string, boolean>;
  hasSubmitted?: boolean;
}

export const CardFormFields = ({
  formData,
  errors,
  nameError,
  onNameChange,
  onFieldChange,
  onFieldBlur,
  getNameErrorType,
  getNameErrorMessage,
  touchedFields = {},
  hasSubmitted = false
}: CardFormFieldsProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <FormFieldWrapper 
        label={t('forms.card.label_name')} 
        required={true} 
        error={getNameErrorMessage(hasSubmitted || touchedFields.name)}
        errorType={getNameErrorType(hasSubmitted || touchedFields.name)}
      >
        <Input
          id="name"
          placeholder={formPlaceholders.cardName}
          value={formData.name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={() => onFieldBlur?.('name')}
          required
        />
      </FormFieldWrapper>

      <FormFieldWrapper 
        label={t('forms.card.label_bank')} 
        required={true} 
        error={(hasSubmitted || touchedFields.bank) ? errors.bank : undefined}
      >
        <Input
          id="bank"
          placeholder={formPlaceholders.bank}
          value={formData.bank}
          onChange={(e) => onFieldChange('bank', e.target.value)}
          onBlur={() => onFieldBlur?.('bank')}
          required
        />
      </FormFieldWrapper>

      <CardAccountField
        value={formData.accountId}
        onChange={(value) => {
          onFieldChange('accountId', value);
          onFieldBlur?.('accountId');
        }}
        error={(hasSubmitted || touchedFields.accountId) && !formData.accountId ? 'Conta vinculada é obrigatória' : undefined}
        required
      />

      <FormFieldWrapper 
        label={t('forms.card.label_limit')} 
        required={true} 
        error={errors.limit}
        errorType={errors.limit ? 'warning' : 'default'}
      >
        <MoneyInput
          id="limit"
          placeholder={formPlaceholders.limit}
          value={formData.limit}
          onChange={(value) => onFieldChange('limit', value)}
          required
        />
      </FormFieldWrapper>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FormFieldWrapper 
          label="Dia de Fechamento" 
          required={true} 
          error={(hasSubmitted || touchedFields.closingDate) ? errors.closingDate : undefined}
        >
          <Input
            id="closingDate"
            type="number"
            min="1"
            max="31"
            placeholder="Ex: 10"
            value={formData.closingDate}
            onChange={(e) => onFieldChange('closingDate', e.target.value)}
            onBlur={() => onFieldBlur?.('closingDate')}
            className="rounded-xl"
            required
          />
        </FormFieldWrapper>

        <FormFieldWrapper 
          label="Dia de Vencimento" 
          required={true} 
          error={(hasSubmitted || touchedFields.dueDate) ? errors.dueDate : undefined}
        >
          <Input
            id="dueDate"
            type="number"
            min="1"
            max="31"
            placeholder="Ex: 15"
            value={formData.dueDate}
            onChange={(e) => onFieldChange('dueDate', e.target.value)}
            onBlur={() => onFieldBlur?.('dueDate')}
            className="rounded-xl"
            required
          />
        </FormFieldWrapper>
      </div>

      <ColorPicker
        value={formData.color}
        onChange={(color) => onFieldChange('color', color)}
        label={t('forms.card.label_color')}
      />
    </div>
  );
};
